import { FormSection, Link, List, Text, Timeline } from '@ksyos/design-system';
import { useSelector } from 'react-redux';
import type { CustomControlCode } from '@common/enums';
import type { IReduxState } from '@frontend/configureStore';

export type HolterExaminationType =
    | CustomControlCode.HolterDirectExaminationRequest
    | CustomControlCode.HolterExternalExaminationRequest
    | CustomControlCode.HolterHomeExaminationRequest
    | CustomControlCode.HolterZorgdomeinExaminationRequest;

type Steps = {
    analyze: string;
    appointment_patient?: string;
    assessment: {
        heading: string;
        text: string;
    };
    optional?: string;
    result: {
        link?: string;
        text: string;
    };
    setup: {
        heading: string;
        steps: string[];
    };
    title: string;
};

const commonText = {
    analyze: (entity: string) =>
        `${entity} leest de recorder uit en laadt de onderzoeksdata in het Ksyos platform, waarna een analyserapport wordt gemaakt`,
    assessment: {
        heading: 'Een Ksyos cardioloog beoordeelt het onderzoek',
        text: 'Bij een ernstige afwijking treedt er een spoedprotocol in werking. De cardioloog beoordeelt direct het onderzoek. Je wordt zo snel mogelijk telefonisch geïnformeerd door Ksyos na uitlezen van de holter.',
    },
    result: {
        link: 'zorgmailnummer hebt doorgegeven',
        text: 'Binnen 3 werkdagen na uitlezen van de recorder ontvang je de uitslag in het Ksyos platform en via Edifact in je HIS als je je ',
    },
    setupSteps: [
        'De patiënt draagt de recorder 1, 2, 3 of 7 dagen',
        'De patiënt noteert de klachten in het dagboekje gedurende het onderzoek',
    ],
};

const holterExaminationFollowingStepsData = {
    HolterDirectExaminationRequest: {
        analyze: commonText.analyze('Ksyos'),
        appointment_patient: 'Je maakt een afspraak met de patiënt voor het aansluiten van de recorder',
        assessment: {
            heading: 'De cardioloog beoordeelt het onderzoek',
            text: commonText.assessment.text,
        },
        result: commonText.result,
        setup: {
            heading: 'Je stelt de recorder in via het Ksyos platform en sluit deze aan bij de patiënt',
            steps: commonText.setupSteps,
        },
        title: 'Vervolgstappen holter/event onderzoek (op de praktijk)',
    },

    HolterExternalExaminationRequest: {
        analyze: commonText.analyze('Het MDC'),
        appointment_patient: 'Het MDC maakt een afspraak met de patiënt voor het aansluiten van de recorder',
        assessment: commonText.assessment,
        optional:
            'Optioneel: je stuurt het onderzoek door naar een Ksyos cardioloog voor advies over het vervolgbeleid (via een consult)',
        result: commonText.result,
        setup: {
            heading: 'Het MDC stelt de recorder in via het Ksyos platform en sluit deze aan bij de patiënt',
            steps: [...commonText.setupSteps, 'De patiënt levert na de afgesproken tijd de recorder in bij het MDC'],
        },
        title: 'Vervolgstappen holter/event onderzoek (extern)',
    },

    HolterHomeExaminationRequest: {
        analyze: commonText.analyze('Ksyos'),
        assessment: commonText.assessment,
        optional:
            'Optioneel: je stuurt het onderzoek door naar een Ksyos cardioloog voor advies over het vervolgbeleid (via een consult)',
        result: commonText.result,
        setup: {
            heading: 'Ksyos stelt de recorder in en stuurt deze naar de patiënt',
            steps: [...commonText.setupSteps, 'De patiënt stuurt na de afgesproken tijd de recorder terug naar Ksyos'],
        },
        title: 'Vervolgstappen holter/event onderzoek (thuis bij de patiënt)',
    },

    HolterZorgdomeinExaminationRequest: {
        analyze: commonText.analyze('Ksyos'),
        assessment: commonText.assessment,
        optional:
            'Optioneel: je stuurt het onderzoek door naar een Ksyos cardioloog voor advies over het vervolgbeleid (via een consult)',
        result: {
            text: 'Binnen 3 werkdagen na uitlezen van de recorder ontvang je de uitslag in het Ksyos platform en via Edifact in je HIS als je je zorgmailnummer hebt doorgegeven',
        },
        setup: {
            heading: 'Ksyos stelt de recorder in en stuurt deze naar de patiënt',
            steps: [...commonText.setupSteps, 'De patiënt stuurt na de afgesproken tijd de recorder terug naar Ksyos'],
        },
        title: 'Vervolgstappen holter/event onderzoek (ZorgDomein)',
    },
};

function renderSteps(stepData: Steps, selectedEmploymentId: string) {
    const { analyze, assessment, result, setup, appointment_patient, optional, title } = stepData;

    return (
        <FormSection title={title}>
            <Timeline>
                {appointment_patient ? <Timeline.Item>{appointment_patient}</Timeline.Item> : <></>}
                <Timeline.Item>
                    <Timeline.ItemHeading>{setup.heading}</Timeline.ItemHeading>
                    <List
                        size="small"
                        styleType="bullet"
                    >
                        {setup.steps.map((step, index) => (
                            <List.Item key={`setupStep${index}`}>{step}</List.Item>
                        ))}
                    </List>
                </Timeline.Item>
                <Timeline.Item>{analyze}</Timeline.Item>
                <Timeline.Item>
                    <Timeline.ItemHeading>{assessment.heading}</Timeline.ItemHeading>
                    <Text
                        as="span"
                        size="sm"
                    >
                        {assessment.text}
                    </Text>
                </Timeline.Item>
                <Timeline.Item>
                    {result.text}{' '}
                    {result.link && (
                        <Link
                            openInNewTab
                            to={`/employments/${selectedEmploymentId}/personalSettings`}
                            variant="link"
                        >
                            {result.link}
                        </Link>
                    )}
                </Timeline.Item>
                {optional ? <Timeline.Item>{optional}</Timeline.Item> : <></>}
            </Timeline>
        </FormSection>
    );
}

export function HolterExaminationFollowingSteps({ holterType }: { holterType: HolterExaminationType }) {
    const holterFollowingStepsData = holterExaminationFollowingStepsData[holterType];

    if (!holterFollowingStepsData) {
        return null;
    }

    return renderSteps(
        holterFollowingStepsData,
        useSelector((state: IReduxState) => state.authentication.selectedEmploymentId)
    );
}
